import { WiseOwlIngress, WiseOwlZendeskRealm, zendeskAppIds } from "../types";
import { getOscConfig } from "./oneSupportCenterEnv/oneSupportCenterEnv";

const { supportApiEndpoint, supportAuthorizerEndpoint, wiseOwlAPIEndpoint } =
  getOscConfig();

export const buildQueryParams = (queryParams?: Record<string, any>): string => {
  const params: Record<string, any> = {};

  // Filter out undefined or null values
  Object.entries(queryParams ?? {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      params[key] = value;
    }
  });
  const urlParams = new URLSearchParams(params).toString();
  return urlParams ? `?${urlParams}` : "";
};

export const wiseOwlEndpoints = {
  // GATED ENDPOINTS
  getAccessToken: (ingress: WiseOwlIngress): string => `/auth/${ingress}`,
  getChats: (queryParams?: { aboutTicketId?: number }) =>
    `/chats${buildQueryParams(queryParams)}`,
  getChatById: (
    chatId: string,
    queryParams?: {
      applicationId?: string;
      interruptedChatId?: string;
    }
  ): string => `/chats/${chatId}${buildQueryParams(queryParams)}`,
  getHome: "/chats/home",
  getHomeByApplicationId: (applicationId: string): string =>
    `/home?applicationId=${applicationId}`,
  createChat: "/chats",
  updateChat: (chatId: string): string => `/chats/${chatId}`,
  createFeedback: (chatId: string, messageId: string) =>
    `/chats/${chatId}/messages/${messageId}/feedback`,
  getChatSummaryById: (chatId: string): string => `/chats/${chatId}/summary`,
  transferToLiveChat: (chatId: string): string =>
    `/chats/${chatId}/liveChat/transfer`,
  // UNGATED ENDPOINTS
  ungatedGetHome: (stargateUngatedUserId: string) =>
    `/ungated/home?stargateUngatedUserId=${stargateUngatedUserId}`,
  ungatedCreateChat: "/ungated/chats",
  ungatedUpdateChat: (chatId: string): string => `/ungated/chats/${chatId}`,
  ungatedCreateFeedback: (chatId: string, messageId: string) =>
    `/ungated/chats/${chatId}/messages/${messageId}/feedback`,
  ungatedGetChatSummaryById: (chatId: string): string =>
    `/ungated/chats/${chatId}/summary`,
};

export const wiseOwlApiDomain = {
  local: "http://localhost:17823/v1",
  dev: "https://comms-gpt.aws-otk-dev-general-use1-001.otk.twilioinfra.com/v1",
  stage:
    "https://comms-gpt.aws-otk-stage-general-use1-001.otk.twilioinfra.com/v1",
  prod: "https://comms-gpt.aws-otk-prod-general-use1-001.otk.twilioinfra.com/v1",
};

export const wiseOwlAppOktaClientIds = {
  local: "0oa1rtqiezvlrSZJ01d8",
  dev: "0oa1rmqayd80Tt6mf1d8",
  stage: "0oa1rmqm9x1NjMYJa1d8",
  prod: "0oa1rmviqmmnCMnUh1d8",
};

export const wiseOwlZendeskAppHostByRealm = {
  local: `localhost:4567`,
  sandbox: `${zendeskAppIds.sandbox}.apps.zdusercontent.com`,
  prod: `${zendeskAppIds.prod}.apps.zdusercontent.com`,
};

export const wiseOwlSalesforceHostByRealm = {
  local: "localhost:4400",
  dev: "wise-owl-salesforce.dev.twilio.com",
  stage: "wise-owl-salesforce.stage.twilio.com",
  prod: "wise-owl-salesforce.twilio.com",
};

export const wiseOwlAppHostByRealm = {
  local: "localhost:4300",
  dev: "wiseowl.dev.twilio.com",
  stage: "wiseowl.stage.twilio.com",
  prod: "wiseowl.twilio.com",
};

export const sendGridAppHostByRealm = {
  local: "localhost:3000",
  stage: "sendgridtest.zendesk.com",
  prod: "support.sendgrid.com",
};

export const wiseOwlZendeskRealmByAppHost = {
  [wiseOwlZendeskAppHostByRealm.local]: WiseOwlZendeskRealm.local,
  [wiseOwlZendeskAppHostByRealm.sandbox]: WiseOwlZendeskRealm.sandbox,
  [wiseOwlZendeskAppHostByRealm.prod]: WiseOwlZendeskRealm.prod,
};

export const wiseOwlAppOktaClientIdByRealm = {
  [wiseOwlAppHostByRealm.local]: wiseOwlAppOktaClientIds.local,
  [wiseOwlAppHostByRealm.dev]: wiseOwlAppOktaClientIds.dev,
  [wiseOwlAppHostByRealm.stage]: wiseOwlAppOktaClientIds.stage,
  [wiseOwlAppHostByRealm.prod]: wiseOwlAppOktaClientIds.prod,
};

export const wiseOwlApiRootByZendeskHost = {
  // [wiseOwlZendeskAppHostByRealm.local]: wiseOwlApiDomain.local,
  [wiseOwlZendeskAppHostByRealm.local]: wiseOwlApiDomain.dev,
  [wiseOwlZendeskAppHostByRealm.sandbox]: wiseOwlApiDomain.stage,
  [wiseOwlZendeskAppHostByRealm.prod]: wiseOwlApiDomain.prod,
};

export const wiseOwlSalesforceServiceHost = {
  [wiseOwlSalesforceHostByRealm.local]: wiseOwlApiDomain.local,
  [wiseOwlSalesforceHostByRealm.dev]: wiseOwlApiDomain.dev,
  [wiseOwlSalesforceHostByRealm.stage]: wiseOwlApiDomain.stage,
  [wiseOwlSalesforceHostByRealm.prod]: wiseOwlApiDomain.prod,
};

export const wiseOwlAppServiceHost = {
  [wiseOwlAppHostByRealm.local]: wiseOwlApiDomain.local,
  [wiseOwlAppHostByRealm.dev]: wiseOwlApiDomain.dev,
  [wiseOwlAppHostByRealm.stage]: wiseOwlApiDomain.stage,
  [wiseOwlAppHostByRealm.prod]: wiseOwlApiDomain.prod,
};

export const getWozdApiServiceEndpoint = () => {
  const serviceEndpoint =
    wiseOwlApiRootByZendeskHost[window.location.host] || wiseOwlApiDomain.prod;

  return serviceEndpoint;
};

export const getWiseOwlAppOktaClientId = () => {
  const oktaClientId =
    wiseOwlAppOktaClientIdByRealm[window.location.host] ||
    wiseOwlAppOktaClientIds.prod;
  return oktaClientId;
};

export const getWiseOwlAppServiceEndpoint = () => {
  const serviceEndpoint =
    wiseOwlAppServiceHost[window.location.host] || wiseOwlApiDomain.prod;

  return serviceEndpoint;
};

export const getWiseOwlSalesforceServiceEndpoint = () => {
  const serviceEndpoint =
    wiseOwlSalesforceServiceHost[window.location.host] || wiseOwlApiDomain.prod;

  return serviceEndpoint;
};

export const wiseOwlIngressToServiceEndpointMap: Record<
  WiseOwlIngress,
  string
> = {
  // Runs through Stargate
  [WiseOwlIngress.osc]: `${supportApiEndpoint}/wiseOwl`,
  [WiseOwlIngress.oscUngated]: `${supportApiEndpoint}/wiseOwl`,
  [WiseOwlIngress.console]: `${supportApiEndpoint}/wiseOwl`,
  // Runs through Support Authorizer
  [WiseOwlIngress.sendGrid]: `${supportAuthorizerEndpoint}/wiseOwl`,
  // Can hit OTK directly
  [WiseOwlIngress.salesforce]: getWiseOwlSalesforceServiceEndpoint(),
  [WiseOwlIngress.zendesk]: getWozdApiServiceEndpoint(),
  [WiseOwlIngress.wiseOwlApp]: `${wiseOwlAPIEndpoint}/proxy/v1`,
};
